import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Card, Popconfirm, Space, Tooltip } from 'antd';
import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  ROUTES_MODULES_KEY,
  SEARCH_PAGINATION_TABLE_FIELDS,
  SORT_ORDER,
} from '../../common/constants';
import CommonTable from '../../components/CommonTable';
import SearchComponent from '../../components/SearchComponent';
import {
  mapAntTableChangeToParams,
  useTableQueryParams,
} from '../../hooks/useTableQueryParams';
import { DELETE_ROLE } from './graphql/Mutations';
import { ROLE_ACCESS } from './graphql/Queries';

const buildRoleFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  searchString: params?.search,
  sortOrder: SORT_ORDER?.[1]?.value,
});

const RoleTable = () => {
  const history = useHistory();
  const { params, setParams } = useTableQueryParams({
    fields: SEARCH_PAGINATION_TABLE_FIELDS,
  });

  const [roleId, setRoleId] = useState('');

  const [executeQuery, { loading, data }] = useLazyQuery(ROLE_ACCESS, {
    fetchPolicy: 'network-only',
    onError: () => {},
  });

  const [deleteRole] = useMutation(DELETE_ROLE, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      executeQuery({
        variables: { filters: buildRoleFilter({ ...params, page: 1 }) },
      });
      setParams({ page: 1 });
    },
    onError: () => {},
  });

  useEffect(() => {
    executeQuery({ variables: { filters: buildRoleFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSearch = debounce((value) => {
    setParams({ search: value?.trim(), page: 1 });
  }, 500);

  const handleDeleteRole = (record) => {
    setRoleId(record?.id);
  };

  const confirm = () => {
    deleteRole({
      variables: {
        where: {
          id: roleId,
        },
      },
    });
  };

  const onChange = (pagination, _filters, sorter) => {
    setParams(mapAntTableChangeToParams(pagination, sorter, params?.pageSize));
  };

  const columns = [
    {
      title: 'Role Name',
      dataIndex: 'name',
      key: 'name',
      width: '70%',
    },
    {
      title: 'Action',
      key: 'action',
      width: '30%',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip placement="bottom" title={<span>Edit Role</span>}>
            <Button
              type="text"
              onClick={(e) => {
                e?.stopPropagation();
                history?.push({
                  pathname: `${ROUTES_MODULES_KEY?.ROLE}/edit/${record?.id}`,
                });
              }}
            >
              <EditOutlined />
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete Role"
            description="Are you sure you want to delete the role?"
            placement="topLeft"
            onConfirm={confirm}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip placement="bottom" title={<span>Delete Role</span>}>
              <Button
                danger
                type="text"
                className={record?.isDefault ? 'visibility-hidden' : ''}
                onClick={(e) => {
                  e?.stopPropagation();
                  handleDeleteRole(record);
                }}
              >
                <DeleteOutlined />
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll"
        title="Users Role"
        extra={
          <>
            <div className="listing-filter">
              <Button
                key="1"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  history?.push({
                    pathname: `${ROUTES_MODULES_KEY?.ROLE}/add`,
                  });
                }}
              >
                Create Role
              </Button>
              <SearchComponent
                key={params?.search || ''}
                defaultValue={params?.search}
                getData={handleSearch}
                name="Role"
              />
            </div>
          </>
        }
      >
        <div className="card-body-wrapper">
          <CommonTable
            size="small"
            columns={columns}
            dataSource={data?.rolePermissions?.data}
            loadingData={loading}
            rowKey={(obj) => obj?.id}
            bordered
            onRow={(record) => ({
              onClick: () =>
                history?.push({
                  pathname: `${ROUTES_MODULES_KEY?.ROLE}/edit/${record?.id}`,
                }),
            })}
            rowClassName="pointer"
            onChange={onChange}
            paginationConfig={{
              current: params?.page,
              total: data?.rolePermissions?.count,
              pageSize: params?.pageSize,
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default RoleTable;
